import { useToast } from '@features/toast/context/toastContext.tsx';
import TOAST_MESSAGE from '@modules/constants/toastMessage.ts';
import { handleApiResponse } from '@src/api/apiClient/axiosClient.ts';
import {
  companyUserApiClient,
  CompanyUserShowResponse,
  CompanyUserUpdateErrorResponse,
  CompanyUserUpdateRequest,
  SuggestEmailErrorResponse,
} from '@src/api/company/auth/companyUserApiClient.ts';
import { companyUserResendInvitationApiClient } from '@src/api/company/auth/companyUserResendInvitationApiClient.ts';
import { companyRoleApiClient } from '@src/api/company/common/companyRole/companyRoleApiClient.ts';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import { fetchCompanyUserProfile } from '@src/context/auth/ProfileFetcher';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { EnableStatus } from '@src/enum/EnableStatus.ts';
import { useScrollToError } from '@src/hooks/useScrollToError';
import { COMPANY_URL } from '@src/routes/company.tsx';
import Button from '@src/ui/elements/button/Button';
import TextLink from '@src/ui/elements/button/TextLink';
import { ErrorMessage } from '@src/ui/elements/form';
import Input from '@src/ui/elements/form/Input';
import Select, { SelectorItem } from '@src/ui/elements/form/Select';
import Title from '@src/ui/elements/typography/Title.tsx';
import ConfirmModal from '@src/ui/templates/Shared/ConfirmModal';
import SuggestEmailUpdateModal from '@src/ui/templates/Shared/SuggestEmailUpdateModal.tsx';
import { firstError, hasError } from '@src/utils/errorMessageUtils.ts';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type FormValues = {
  familyName: string;
  firstName: string;
  familyNameKana: string;
  firstNameKana: string;
  email: string;
  birthday: string;
  companyRoleId: string;
  enabled: string;
};

type RoleSelector = SelectorItem[];
type StatusSelector = SelectorItem[];

export type SuggestFormValues = {
  suggestedEmail: string;
};

const CompanyUserEditForm: React.FC = () => {
  const navigate = useNavigate();
  const { profile, setProfile } = useCompanyAuth();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [companyUserShowResponse, setCompanyUserShowResponse] =
    useState<CompanyUserShowResponse | null>(null);
  const [formValues, setFormValues] = useState<FormValues | null>(null);
  const [errors, setErrors] = useState<CompanyUserUpdateErrorResponse>();
  const [suggestValues, setSuggestValues] = useState<SuggestFormValues>({
    suggestedEmail: '',
  });
  const [suggestErrors, setSuggestErrors] = useState<SuggestEmailErrorResponse>();
  const [roleSelector, setRoleSelector] = useState<RoleSelector>();
  const [statusSelector, setStatusSelector] = useState<StatusSelector>();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [resendInvitationDisabled, setResendInvitationDisabled] = useState<boolean>(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpenSuggestModal, setIsOpenSuggestModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const res = await companyUserApiClient.show(Number(id));
      handleApiResponse(res, async (data) => {
        setCompanyUserShowResponse(data);

        const initialFormValues: FormValues = {
          familyName: data.family_name,
          firstName: data.first_name,
          familyNameKana: data.family_name_kana,
          firstNameKana: data.first_name_kana,
          email: data.email,
          birthday: data.birthday,
          companyRoleId: String(data.company_role_id),
          enabled: data.enabled ? 'ENABLED' : 'DISABLED',
        };
        setFormValues(initialFormValues);
      });
    };
    fetchData();

    const fetchCompanyRoles = async () => {
      const res = await companyRoleApiClient.index();
      handleApiResponse(res, (data) => {
        setRoleSelector(
          data.map((role) => {
            return {
              id: String(role.id),
              name: role.name,
            };
          }),
        );
      });
    };
    fetchCompanyRoles();

    setStatusSelector([
      { id: 'ENABLED', name: EnableStatus.ENABLED },
      { id: 'DISABLED', name: EnableStatus.DISABLED },
    ]);
  }, [id]);

  const submit = async () => {
    if (!formValues || submitted) {
      return;
    }
    setErrors(undefined);
    setSubmitted(true);

    const params: CompanyUserUpdateRequest = {
      company_user: {
        family_name: formValues.familyName,
        first_name: formValues.firstName,
        family_name_kana: formValues.familyNameKana,
        first_name_kana: formValues.firstNameKana,
        birthday: formValues.birthday,
        company_role_id: formValues.companyRoleId,
        enabled: formValues?.enabled ? formValues?.enabled === 'ENABLED' : undefined,
      },
    };
    const response = await companyUserApiClient.update(Number(id), params);

    handleApiResponse(
      response,
      async () => {
        toast(TOAST_MESSAGE.UPDATE);
        // 自分自身を更新した場合、認証情報を再取得
        if (profile?.user?.id && Number(id) === profile.user.id) {
          await fetchCompanyUserProfile(setProfile);
        }
        navigate(COMPANY_URL.COMPANY_USERS);
      },
      (errors) => {
        setErrors(errors);
        setSubmitted(false);
      },
    );
  };

  const handleClickCancel = () => {
    navigate(COMPANY_URL.COMPANY_USERS);
  };

  const handleClickResendInvitation = async () => {
    if (!companyUserShowResponse || resendInvitationDisabled) {
      return;
    }
    setResendInvitationDisabled(true);

    const response = await companyUserResendInvitationApiClient.resendInvitation(
      Number(companyUserShowResponse.id),
      {},
    );
    handleApiResponse(response, () => {
      toast(TOAST_MESSAGE.RESEND);
    });

    setTimeout(() => {
      setResendInvitationDisabled(false);
    }, 60 * 1000);
  };

  const handleClickDelete = async () => {
    setIsOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!companyUserShowResponse) {
      return;
    }

    const response = await companyUserApiClient.delete(Number(companyUserShowResponse.id));
    handleApiResponse(response, () => {
      toast(TOAST_MESSAGE.DELETE);
      setIsOpenDeleteModal(false);
      navigate(COMPANY_URL.COMPANY_USERS);
    });
  };

  const handleCancelDelete = () => {
    setIsOpenDeleteModal(false);
  };

  const handleClickSuggest = () => {
    setSuggestErrors(undefined);
    setIsOpenSuggestModal(true);
  };

  const handleSuggestValuesChange = (newValues: Partial<SuggestFormValues>) => {
    setSuggestValues((prev) => ({ ...prev, ...newValues }));
  };

  const handleCancelSuggest = () => {
    setIsOpenSuggestModal(false);
    setSuggestValues({ suggestedEmail: '' });
  };

  const onSuggest = async () => {
    setLoading(true);
    setSuggestErrors(undefined);

    if (!companyUserShowResponse) {
      return;
    }

    const res = await companyUserApiClient.sendSuggestion(Number(companyUserShowResponse.id), {
      family_name: companyUserShowResponse.family_name,
      first_name: companyUserShowResponse.first_name,
      suggested_email: suggestValues.suggestedEmail,
    });

    handleApiResponse(
      res,
      () => {
        setIsOpenSuggestModal(false);
        setSuggestValues({ suggestedEmail: '' });
        toast(TOAST_MESSAGE.SEND);
      },
      (errors) => {
        setSuggestErrors(errors);
      },
    );
    setLoading(false);
  };

  const fieldRefs = useScrollToError(errors ?? {});

  return (
    <main className='px-6 pb-12 pt-2'>
      <SpinnerOverlay loading={loading} />
      <div>
        <div className='mt-4 flex flex-col'>
          <div className='flex items-center justify-between gap-4'>
            <Title>ユーザー基本情報</Title>
            {companyUserShowResponse && (
              <div className='flex items-center gap-6'>
                {companyUserShowResponse.can_resend_invitation && (
                  <Button
                    variant='primary'
                    disabled={resendInvitationDisabled}
                    onClick={handleClickResendInvitation}
                  >
                    招待メールを再送する
                  </Button>
                )}
                {profile?.user?.id !== companyUserShowResponse.id && (
                  <button
                    type='button'
                    className='text-sm text-warning-500 underline'
                    onClick={handleClickDelete}
                  >
                    ユーザーを削除する
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        {formValues && (
          <div className='mx-auto max-w-[800px] py-[60px]'>
            <form
              className='mt-10 flex flex-col gap-[26px]'
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>氏名</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company_user.family_name'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='山田'
                      value={formValues.familyName}
                      onChange={(e) => setFormValues({ ...formValues, familyName: e.target.value })}
                      error={hasError(errors?.company_user.family_name)}
                    />
                    <ErrorMessage>{firstError(errors?.company_user.family_name)}</ErrorMessage>
                  </div>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company_user.first_name'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='太郎'
                      value={formValues.firstName}
                      onChange={(e) => setFormValues({ ...formValues, firstName: e.target.value })}
                      error={hasError(errors?.company_user.first_name)}
                    />
                    <ErrorMessage>{firstError(errors?.company_user.first_name)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>氏名（フリガナ）</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company_user.family_name_kana'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='ヤマダ'
                      value={formValues.familyNameKana}
                      onChange={(e) =>
                        setFormValues({ ...formValues, familyNameKana: e.target.value })
                      }
                      error={hasError(errors?.company_user.family_name_kana)}
                    />
                    <ErrorMessage>{firstError(errors?.company_user.family_name_kana)}</ErrorMessage>
                  </div>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['company_user.first_name_kana'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='タロウ'
                      value={formValues.firstNameKana}
                      onChange={(e) =>
                        setFormValues({ ...formValues, firstNameKana: e.target.value })
                      }
                      error={hasError(errors?.company_user.first_name_kana)}
                    />
                    <ErrorMessage>{firstError(errors?.company_user.first_name_kana)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>メールアドレス</label>
                </div>
                <div className='flex gap-3'>
                  <label className=''>{companyUserShowResponse?.email}</label>
                  <a
                    className='cursor-pointer text-primary-500 underline'
                    onClick={handleClickSuggest}
                  >
                    変更提案
                  </a>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>生年月日</label>
                </div>
                <div
                  className='flex max-w-[200px]'
                  ref={(el) => (fieldRefs.current['company_user.birthday'] = el)}
                >
                  <Input
                    size='m'
                    type='text'
                    placeholder='19900101'
                    value={formValues.birthday}
                    onChange={(e) => setFormValues({ ...formValues, birthday: e.target.value })}
                    error={hasError(errors?.company_user.birthday)}
                    inputMode='numeric'
                  />
                </div>
                <ErrorMessage>{firstError(errors?.company_user.birthday)}</ErrorMessage>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex gap-4'>
                  <div className='flex w-full flex-col gap-[6px]'>
                    <div className='flex items-center gap-3'>
                      <label className=''>権限</label>
                      <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                        必須
                      </div>
                      <span className='text-xs'>
                        詳細は
                        <TextLink
                          text='こちら'
                          onClick={() =>
                            window.open(
                              `${import.meta.env.VITE_HELP_BASE_URL}${import.meta.env.VITE_COMPANY_ROLE_GUIDE_URL}`,
                              '_blank',
                            )
                          }
                        />
                      </span>
                    </div>
                    <div
                      ref={(el) => (fieldRefs.current['company_user.company_role_id'] = el)}
                      className='flex w-full flex-col'
                    >
                      {roleSelector && (
                        <Select
                          items={roleSelector}
                          size='m'
                          initialValue={formValues.companyRoleId}
                          onSelect={(value) =>
                            setFormValues({ ...formValues, companyRoleId: value })
                          }
                          error={hasError(errors?.company_user.company_role_id)}
                        />
                      )}
                    </div>
                    <ErrorMessage>{firstError(errors?.company_user.company_role_id)}</ErrorMessage>
                  </div>
                  <div className='flex w-full flex-col gap-[6px]'>
                    <div className='flex items-center gap-3'>
                      <label className=''>ステータス</label>
                      <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                        必須
                      </div>
                    </div>
                    <div
                      ref={(el) => (fieldRefs.current['company_user.enabled'] = el)}
                      className='flex w-full flex-col'
                    >
                      {statusSelector && (
                        <Select
                          items={statusSelector}
                          size='m'
                          initialValue={formValues.enabled}
                          onSelect={(value) => setFormValues({ ...formValues, enabled: value })}
                          error={hasError(errors?.company_user.enabled)}
                        />
                      )}
                    </div>
                    <ErrorMessage>{firstError(errors?.company_user.enabled)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='mt-auto flex justify-center gap-5 pt-4'>
                <div className='w-[172px]'>
                  <Button
                    variant='secondary'
                    fullRounded
                    fullWidth
                    outline
                    onClick={handleClickCancel}
                  >
                    キャンセル
                  </Button>
                </div>
                <div className='w-[172px]'>
                  <Button
                    fullRounded
                    fullWidth
                    outline
                    onClick={submit}
                  >
                    保存する
                  </Button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
      <ConfirmModal
        title='完全に削除しますか？'
        message='ユーザーが完全に削除されますがよろしいですか？'
        confirmText='完全に削除'
        cancelText='戻る'
        isOpen={isOpenDeleteModal}
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
        isDanger={true}
      />
      {isOpenSuggestModal && (
        <SuggestEmailUpdateModal
          values={suggestValues}
          onChange={handleSuggestValuesChange}
          onSuggest={onSuggest}
          onClose={handleCancelSuggest}
          isOpen={isOpenSuggestModal}
          errors={suggestErrors}
        />
      )}
    </main>
  );
};

export default CompanyUserEditForm;
